import { takeLatest, all, put, select } from 'redux-saga/effects';

import { requestAndSet, sendToHelium } from 'utils/helpers';

import { ApiResponse } from '../../types/apiTypes';
import {
  VisitorWorkflowConfig,
  VisitorWorkflowFields,
} from '../../types/visitorWorkflowTypes';
import {
  visitorWorkflowsActions,
  VisitorWorkflowsState,
} from '../slices/visitorWorkflowsSlice';

type VisitorWorkflowLoadingKey = ReturnType<
  typeof visitorWorkflowsActions.setLoading
>['payload']['key'];

const loading = (key: VisitorWorkflowLoadingKey) => (v: boolean) =>
  visitorWorkflowsActions.setLoading({ key, value: v });

function* fetchVisitorWorkflowTypesSaga(
  action: ReturnType<typeof visitorWorkflowsActions.fetchVisitorWorkflowTypes>,
): Generator {
  const { orgId } = action.payload;
  yield requestAndSet('listAllVisitorWorkflowTypes', [orgId], {
    createSetterAction: visitorWorkflowsActions.setVisitorWorkflowTypes,
    loadingAction: loading('fetchWorkflowTypesLoading'),
  });
}

function* fetchVisitorWorkflowsSaga(
  action: ReturnType<typeof visitorWorkflowsActions.fetchVisitorWorkflows>,
): Generator {
  const { orgId, workflowTypeId } = action.payload;
  yield requestAndSet('listAllVisitorWorkflow', [orgId], {
    queryStringParams: { ...(workflowTypeId && { workflowTypeId }) },
    createSetterAction: visitorWorkflowsActions.setVisitorWorkflows,
    loadingAction: loading('fetchVisitorWorkflowsLoading'),
  });
}

function* describeVisitorWorkflowSaga(
  action: ReturnType<typeof visitorWorkflowsActions.describeVisitorWorkflow>,
): Generator {
  const { orgId, visitorWorkflowId } = action.payload;
  yield requestAndSet('describeVisitorWorkflow', [orgId, visitorWorkflowId], {
    createSetterAction: visitorWorkflowsActions.setSelectedVisitorWorkflow,
    loadingAction: loading('describeVisitorWorkflowLoading'),
  });
}

function* createVisitorWorkflowSaga(
  action: ReturnType<typeof visitorWorkflowsActions.createVisitorWorkflow>,
): Generator {
  const { orgId, visitorWorkflow } = action.payload;
  try {
    yield sendToHelium('insertVisitorWorkflow', [orgId], visitorWorkflow, {
      loadingAction: loading('createVisitorWorkflowLoading'),
    });
    yield put(visitorWorkflowsActions.fetchVisitorWorkflows({ orgId }));
  } finally {
    yield put(
      visitorWorkflowsActions.setLoading({
        key: 'createVisitorWorkflowLoading',
        value: false,
      }),
    );
  }
}

function* updateVisitorWorkflowSaga(
  action: ReturnType<typeof visitorWorkflowsActions.updateVisitorWorkflow>,
): Generator {
  const { orgId, visitorWorkflowId, visitorWorkflow } = action.payload;
  yield sendToHelium(
    'updateVisitorWorkflow',
    [orgId, visitorWorkflowId],
    visitorWorkflow,
    {
      loadingAction: loading('updateVisitorWorkflowLoading'),
    },
  );
  yield put(visitorWorkflowsActions.fetchVisitorWorkflows({ orgId }));
}

function* deleteVisitorWorkflowSaga(
  action: ReturnType<typeof visitorWorkflowsActions.deleteVisitorWorkflow>,
): Generator {
  const { orgId, visitorWorkflowId } = action.payload;
  yield sendToHelium(
    'deleteVisitorWorkflow',
    [orgId, visitorWorkflowId],
    {},
    {
      loadingAction: loading('deleteVisitorWorkflowLoading'),
    },
  );
  yield put(visitorWorkflowsActions.fetchVisitorWorkflows({ orgId }));
}

function* describeVisitorWorkflowLocationsSaga(
  action: ReturnType<
    typeof visitorWorkflowsActions.describeVisitorWorkflowLocations
  >,
): Generator {
  const { orgId, visitorWorkflowId } = action.payload;
  yield requestAndSet(
    'getVisitorWorkflowLocations',
    [orgId, visitorWorkflowId],
    {
      createSetterAction: visitorWorkflowsActions.setVisitorWorkflowLocations,
      loadingAction: loading('describeVisitorWorkflowLocationsLoading'),
    },
  );
}

function* describeVisitorWorkflowConfigSaga(
  action: ReturnType<
    typeof visitorWorkflowsActions.describeVisitorWorkflowConfig
  >,
): Generator {
  const { orgId, visitorWorkflowId, global } = action.payload;
  yield requestAndSet('getVisitorWorkflowConfig', [orgId, visitorWorkflowId], {
    createSetterAction: (res: unknown) =>
      visitorWorkflowsActions.setVisitorWorkflowConfig({
        ...(res as ApiResponse<VisitorWorkflowConfig>),
        global,
      }),
    loadingAction: loading('describeVisitorWorkflowConfigLoading'),
  });
}

function* updateVisitorWorkflowConfigSaga(
  action: ReturnType<
    typeof visitorWorkflowsActions.updateVisitorWorkflowConfig
  >,
): Generator {
  const { orgId, visitorWorkflowId, visitorWorkflowConfigParams } =
    action.payload;
  try {
    yield put(
      visitorWorkflowsActions.setLoading({
        key: 'updateVisitorWorkflowConfigLoading',
        value: true,
      }),
    );
    yield sendToHelium(
      'updateVisitorWorkflowConfig',
      [orgId, visitorWorkflowId],
      visitorWorkflowConfigParams,
      {},
    );

    yield put(
      visitorWorkflowsActions.describeVisitorWorkflowConfig({
        orgId,
        visitorWorkflowId,
      }),
    );

    const state = (yield select((s: { get: (key: string) => unknown }) =>
      s.get('visitorWorkflows'),
    )) as VisitorWorkflowsState;
    const globalSignInWorkflowConfig = state.globalSignInWorkflowConfig;

    if (visitorWorkflowId === globalSignInWorkflowConfig?.id) {
      yield put(
        visitorWorkflowsActions.describeVisitorWorkflowConfig({
          orgId,
          visitorWorkflowId,
          global: true,
        }),
      );
    }
  } catch {
    // Config Update Error
  } finally {
    yield put(
      visitorWorkflowsActions.setLoading({
        key: 'updateVisitorWorkflowConfigLoading',
        value: false,
      }),
    );
  }
}

function* describeVisitorWorkflowFieldsSaga(
  action: ReturnType<
    typeof visitorWorkflowsActions.describeVisitorWorkflowFields
  >,
): Generator {
  const { orgId, visitorWorkflowId, global } = action.payload;
  yield requestAndSet('getVisitorWorkflowFields', [orgId, visitorWorkflowId], {
    createSetterAction: (res: unknown) =>
      visitorWorkflowsActions.setVisitorWorkflowFields({
        ...(res as ApiResponse<VisitorWorkflowFields>),
        global,
      }),
    loadingAction: loading('describeVisitorWorkflowFieldsLoading'),
  });
}

function* updateVisitorWorkflowFieldsSaga(
  action: ReturnType<
    typeof visitorWorkflowsActions.updateVisitorWorkflowFields
  >,
): Generator {
  const { orgId, visitorWorkflowId, visitorWorkflowFields } = action.payload;
  try {
    yield put(
      visitorWorkflowsActions.setLoading({
        key: 'updateVisitorWorkflowFieldsLoading',
        value: true,
      }),
    );
    yield sendToHelium(
      'updateVisitorWorkflowFields',
      [orgId, visitorWorkflowId],
      visitorWorkflowFields,
      {},
    );

    yield put(
      visitorWorkflowsActions.describeVisitorWorkflowFields({
        orgId,
        visitorWorkflowId,
      }),
    );

    const state = (yield select((s: { get: (key: string) => unknown }) =>
      s.get('visitorWorkflows'),
    )) as VisitorWorkflowsState;
    const globalSignInWorkflowConfig = state.globalSignInWorkflowConfig;

    if (visitorWorkflowId === globalSignInWorkflowConfig?.id) {
      yield put(
        visitorWorkflowsActions.describeVisitorWorkflowFields({
          orgId,
          visitorWorkflowId,
          global: true,
        }),
      );
    }
  } catch {
    // Fields Update Error
  } finally {
    yield put(
      visitorWorkflowsActions.setLoading({
        key: 'updateVisitorWorkflowFieldsLoading',
        value: false,
      }),
    );
  }
}

function* fetchVisitorWorkflowAgreementsSaga(
  action: ReturnType<
    typeof visitorWorkflowsActions.fetchVisitorWorkflowAgreements
  >,
): Generator {
  const { orgId, visitorWorkflowId } = action.payload;
  yield requestAndSet(
    'getVisitorWorkflowDocuments',
    [orgId, visitorWorkflowId],
    {
      createSetterAction: visitorWorkflowsActions.setVisitorAgreements,
      loadingAction: loading('fetchVisitorAgreementsLoading'),
    },
  );
}

function* bulkUpdateVisitorWorkflowAgreementsSaga(
  action: ReturnType<
    typeof visitorWorkflowsActions.bulkUpdateVisitorWorkflowAgreements
  >,
): Generator {
  const { orgId, visitorWorkflowId, add, remove } = action.payload;
  try {
    yield sendToHelium(
      'updateVisitorWorkflowDocuments',
      [orgId, visitorWorkflowId],
      { add, remove },
      {
        loadingAction: loading('bulkUpdateVisitorAgreementsLoading'),
      },
    );
    yield put(
      visitorWorkflowsActions.describeVisitorWorkflow({
        orgId,
        visitorWorkflowId,
      }),
    );
    yield put(
      visitorWorkflowsActions.fetchVisitorWorkflows({
        orgId,
      }),
    );
  } finally {
    yield put(
      visitorWorkflowsActions.setLoading({
        key: 'bulkUpdateVisitorAgreementsLoading',
        value: false,
      }),
    );
  }
}

// --- Watcher Saga ---
export default function* visitorWorkflowsSaga(): Generator {
  yield all([
    takeLatest(
      visitorWorkflowsActions.fetchVisitorWorkflowTypes.type,
      fetchVisitorWorkflowTypesSaga,
    ),
    takeLatest(
      visitorWorkflowsActions.fetchVisitorWorkflows.type,
      fetchVisitorWorkflowsSaga,
    ),
    takeLatest(
      visitorWorkflowsActions.describeVisitorWorkflow.type,
      describeVisitorWorkflowSaga,
    ),
    takeLatest(
      visitorWorkflowsActions.createVisitorWorkflow.type,
      createVisitorWorkflowSaga,
    ),
    takeLatest(
      visitorWorkflowsActions.updateVisitorWorkflow.type,
      updateVisitorWorkflowSaga,
    ),
    takeLatest(
      visitorWorkflowsActions.deleteVisitorWorkflow.type,
      deleteVisitorWorkflowSaga,
    ),
    takeLatest(
      visitorWorkflowsActions.describeVisitorWorkflowLocations.type,
      describeVisitorWorkflowLocationsSaga,
    ),
    takeLatest(
      visitorWorkflowsActions.describeVisitorWorkflowConfig.type,
      describeVisitorWorkflowConfigSaga,
    ),
    takeLatest(
      visitorWorkflowsActions.updateVisitorWorkflowConfig.type,
      updateVisitorWorkflowConfigSaga,
    ),
    takeLatest(
      visitorWorkflowsActions.describeVisitorWorkflowFields.type,
      describeVisitorWorkflowFieldsSaga,
    ),
    takeLatest(
      visitorWorkflowsActions.updateVisitorWorkflowFields.type,
      updateVisitorWorkflowFieldsSaga,
    ),
    takeLatest(
      visitorWorkflowsActions.fetchVisitorWorkflowAgreements.type,
      fetchVisitorWorkflowAgreementsSaga,
    ),
    takeLatest(
      visitorWorkflowsActions.bulkUpdateVisitorWorkflowAgreements.type,
      bulkUpdateVisitorWorkflowAgreementsSaga,
    ),
  ]);
}
